import { startRegistration } from '@simplewebauthn/browser';
import type {
	PublicKeyCredentialCreationOptionsJSON,
	RegistrationResponseJSON,
} from '@simplewebauthn/typescript-types';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { showToast } from '@app/providers/ToastProvider';
import { getCurrentUser } from '@app/redux/auth/auth.selectors';

function useWebAuthn({ onDecline }: { onDecline: () => void }) {
	const { startRequest: addWebAuthnMethod } = useTriggeredRequest(AccountClientRequests.MFA.addWebAuthnMethod);
	const { startRequest: enable2FA } = useTriggeredRequest(AccountClientRequests.MFA.toggleMFA);
	const { startRequest: getCredsWebAuthnMethod } = useTriggeredRequest(
		AccountClientRequests.MFA.getCredsWebAuthnMethod,
	);
	const [isLoading, setIsLoading] = useState(false);
	const currentUser = useSelector(getCurrentUser);

	const registerWebAuthn2FA = async () => {
		setIsLoading(true);
		try {
			const publicKey = await getCredsWebAuthnMethod().then(
				({ public_key }) => camelizeKeys(public_key) as PublicKeyCredentialCreationOptionsJSON,
			);
			const creds = await startRegistration(publicKey).then(creds => {
				const decamelizeCreds = decamelizeKeys(creds) as RegistrationResponseJSON;
				return {
					...decamelizeCreds,
					response: { ...decamelizeCreds.response, client_data_json: creds.response.clientDataJSON },
				};
			});
			await addWebAuthnMethod({ name: `WebAuthn_${currentUser?.email}_${Date.now()}`, ...creds }).then(() =>
				showToast.success('WebAuthn verification is successfully done'),
			);
			await enable2FA({ mfa_enabled: true }).then(() => showToast.success('2FA has been enabled'));
		} catch (error) {
			if (error instanceof Error && error.name === 'NotAllowedError') onDecline();
			else showToast.error('Oops. Something went wrong.');
		} finally {
			setIsLoading(false);
		}
	};

	return { registerWebAuthn2FA, isLoading };
}

export default useWebAuthn;
